import type { ScopeContext } from '@sentry/types'
import {
  captureConsoleIntegration,
  captureException,
  extraErrorDataIntegration,
  httpClientIntegration,
  init,
  replayIntegration,
  setContext as setSentryContext,
  setUser as setSentryUser,
  thirdPartyErrorFilterIntegration,
} from '@sentry/vue'
import type { App } from 'vue'

import { CONFIG } from '../../config'

export function setupErrorTracking(app: App<Element>): void {
  if (import.meta.env.DEV) {
    return
  }
  init({
    app,
    environment: CONFIG.SDK.SENTRY.ENV,
    release: globalThis.__VERSION__,
    dsn: CONFIG.SDK.SENTRY.DSN,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.2,
    integrations: [
      captureConsoleIntegration({ levels: ['warn', 'error', 'assert'] }),
      httpClientIntegration(),
      extraErrorDataIntegration({ captureErrorCause: true }),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
        networkCaptureBodies: true,
        networkDetailAllowUrls: [globalThis.location.origin],
      }),
      thirdPartyErrorFilterIntegration({
        filterKeys: ['response-submission'],
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    ],
    beforeSend: (event) => {
      if (globalThis.location.hostname === 'localhost') {
        return null
      }
      return event
    },
  })
}

export function setUser({ id, segment }: { id: string; segment?: string }): void {
  setSentryUser({ id, segment })
}

type InterviewContext = {
  roundId: number | undefined
  currentProgress: number | undefined
  audienceType: string | undefined
}

type PromptContext =
  | Readonly<{ type: 'roundLanding'; round: RoundLandingContext }>
  | Readonly<{ type: 'question'; question: QuestionContext }>
  | Readonly<{ type: 'message'; message: MessageContext }>
  | Readonly<{ type: 'consent' }>
  | Readonly<{ type: 'preVideoTestCard' }>
  | Readonly<{ type: 'videoTest'; question: QuestionContext }>
  | Readonly<{ type: 'preVideoMessage' }>

type QuestionContext = {
  id: number
  type: string
  media:
    | {
        playbackId: string
        playbackToken: string | undefined
        thumbnailToken: string | undefined
        type: 'mux'
      }
    | {
        url: string
        type: 'audio' | 'video' | 'image'
      }
    | undefined
}

type MessageContext = {
  media:
    | {
        url: string
        type: 'image'
      }
    | undefined
}

type RoundLandingContext = {
  id: number
  progress: number
}

type Contexts = {
  interview: InterviewContext
  prompt: PromptContext
}

export function setContext<T extends keyof Contexts>(
  name: T,
  context: Contexts[T] | undefined,
): void {
  setSentryContext(name, context ?? null)
}

export function capture(error: Error, context?: Partial<ScopeContext>): void {
  setGlobalContext()
  captureException(error, context)
}

export function setGlobalContext(): void {
  setSentryContext('global', {
    hostLocation: !window.parent
      ? undefined
      : globalThis.location !== window.parent.location
        ? document.referrer
        : document.location.href,
  })
}
